import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlogBackToTopButton from '../components/BlogBackToTopButton';
import { apiUrl } from '../config';

const CATEGORY_ICONS = ['restaurant_menu', 'quiz', 'help', 'lightbulb', 'kitchen'];

export default function FaqPage({ isDarkMode, toggleDarkMode, embedded = false }) {
  const [categories, setCategories] = useState([]);
  const [activeCategoryKey, setActiveCategoryKey] = useState('');
  const [activeItemId, setActiveItemId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voteState, setVoteState] = useState({});
  const [pendingScrollItemId, setPendingScrollItemId] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactResult, setContactResult] = useState(null);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    email: '',
    consentAccepted: false,
    website: ''
  });

  const authToken = useMemo(
    () => localStorage.getItem('authToken') || localStorage.getItem('token') || '',
    []
  );
  const isInternalUser = Boolean(authToken);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(apiUrl('/api/faqs'));
        if (!res.ok) throw new Error('faq_fetch_failed');
        const data = await res.json();
        const rows = Array.isArray(data?.categories) ? data.categories : [];
        if (!mounted) return;

        setCategories(rows);
        const firstCategory = rows[0] || null;
        const firstItem = firstCategory?.items?.[0] || null;
        setActiveCategoryKey(firstCategory?.key || '');
        setActiveItemId(firstItem?.id || null);
      } catch (_err) {
        if (!mounted) return;
        setError('SSS verileri alınamadı. Lütfen daha sonra tekrar deneyin.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // [SEO] Sayfa-özel title/description/canonical (SPA içi gezinmede de doğru olsun; SSR ile aynı değerler).
  // Aksi halde /sss, index.html default'undan ana sayfa başlığını + ana sayfa canonical'ını miras alıyordu.
  useEffect(() => {
    if (embedded) return undefined;
    const origin = window.location.origin;
    const title = 'Sıkça Sorulan Sorular (SSS) | Bana Bir Tarif Ver';
    const desc = 'Bana Bir Tarif Ver hakkında en sık sorulan sorular: stok yönetimi, bütçe, haftalık yemek planı, alerjen güvenliği ve bebekli aile ihtiyaçları.';
    document.title = title;
    const setMeta = (selector, attr, key, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    setMeta('meta[name="description"]', 'name', 'description', desc);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', desc);
    setMeta('meta[property="og:url"]', 'property', 'og:url', `${origin}/sss`);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${origin}/sss`);
    return undefined;
  }, [embedded]);

  const activeCategory = useMemo(
    () => categories.find((cat) => cat.key === activeCategoryKey) || categories[0] || null,
    [categories, activeCategoryKey]
  );

  const categoryItems = useMemo(
    () => (Array.isArray(activeCategory?.items) ? activeCategory.items : []),
    [activeCategory]
  );

  const activeItem = useMemo(
    () => categoryItems.find((item) => item.id === activeItemId) || categoryItems[0] || null,
    [categoryItems, activeItemId]
  );

  const selectCategory = (key, scrollToFirstItem = false) => {
    const found = categories.find((cat) => cat.key === key);
    setActiveCategoryKey(key);
    const firstItemId = found?.items?.[0]?.id || null;
    setActiveItemId(firstItemId);
    if (scrollToFirstItem && firstItemId) setPendingScrollItemId(firstItemId);
  };

  useEffect(() => {
    if (!pendingScrollItemId) return;
    const el =
      document.getElementById(`faq-mobile-answer-${pendingScrollItemId}`) ||
      document.getElementById(`faq-answer-${pendingScrollItemId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingScrollItemId(null);
    }
  }, [pendingScrollItemId, activeCategoryKey, categoryItems]);

  const selectItem = (itemId) => {
    setActiveItemId(itemId);
    const el =
      document.getElementById(`faq-mobile-answer-${itemId}`) ||
      document.getElementById(`faq-answer-${itemId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sendVote = async (faqItemId, vote) => {
    try {
      const anonSessionId = `faq_${localStorage.getItem('anonSessionId') || Math.random().toString(36).slice(2)}`;
      localStorage.setItem('anonSessionId', anonSessionId.replace('faq_', ''));
      await fetch(apiUrl(`/api/faqs/${faqItemId}/feedback`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote, anonSessionId, source: 'faq_page' })
      });
      setVoteState((prev) => ({ ...prev, [faqItemId]: vote }));
    } catch (_err) {
      // Silent fail on UI
    }
  };

  const resetContactForm = () => {
    setContactForm({
      subject: '',
      message: '',
      email: '',
      consentAccepted: false,
      website: ''
    });
  };

  const openContactModal = () => {
    setContactResult(null);
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const submitContactForm = async (event) => {
    event.preventDefault();
    setContactResult(null);
    const subject = String(contactForm.subject || '').trim();
    const message = String(contactForm.message || '').trim();
    const email = String(contactForm.email || '').trim();
    const website = String(contactForm.website || '');

    if (!subject || subject.length > 160) {
      setContactResult({ ok: false, message: 'Konu zorunlu ve 160 karakteri gecmemeli.' });
      return;
    }
    if (!message || message.length < 10 || message.length > 2000) {
      setContactResult({ ok: false, message: 'Mesaj 10 ile 2000 karakter arasinda olmali.' });
      return;
    }
    if (!isInternalUser) {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setContactResult({ ok: false, message: 'Gecerli bir e-posta adresi girin.' });
        return;
      }
      if (!contactForm.consentAccepted) {
        setContactResult({ ok: false, message: 'KVKK onayini isaretlemeniz gerekiyor.' });
        return;
      }
    }

    try {
      setIsContactSubmitting(true);
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;

      const response = await fetch(apiUrl('/api/faq-contact'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          subject,
          message,
          email: isInternalUser ? undefined : email,
          consentAccepted: isInternalUser ? true : Boolean(contactForm.consentAccepted),
          faqItemId: activeItem?.id || null,
          website
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setContactResult({
          ok: false,
          message: payload?.message || 'Mesaj gonderilemedi. Lutfen tekrar deneyin.'
        });
        return;
      }

      setContactResult({
        ok: true,
        message: isInternalUser
          ? 'Talebiniz alindi. Uygulama icinden bilgilendirileceksiniz.'
          : 'Talebiniz alindi. E-posta uzerinden donus yapilacaktir.'
      });
      resetContactForm();
    } catch (_err) {
      setContactResult({ ok: false, message: 'Mesaj gonderilemedi. Lutfen tekrar deneyin.' });
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <div
      id="top"
      className={
        embedded
          ? 'min-h-full bg-transparent text-on-surface dark:text-white'
          : 'min-h-screen bg-background text-on-surface dark:bg-[#0a0d10] dark:text-white'
      }
    >
      {!embedded ? <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} /> : null}

      <main className={`mx-auto w-full max-w-7xl px-6 pb-24 ${embedded ? 'pt-8' : 'pt-32'}`}>
        <section className="mb-12 border-b border-slate-200 pb-8 dark:border-slate-800">
          <div className="mb-5 flex items-center justify-between">
            <div />
            {!embedded ? (
              <Link
                to="/"
                className="font-label rounded-full border border-slate-300 px-4 py-2 text-xs font-bold uppercase tracking-wider text-on-surface hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-200"
              >
                Ana Sayfaya Dön
              </Link>
            ) : null}
          </div>
          <p className="font-label mb-3 text-xs font-bold uppercase tracking-[0.22em] text-primary">Destek Merkezi</p>
          <h1 className="font-display text-3xl font-bold leading-tight text-on-surface md:text-6xl dark:text-white">
            Mutfaktaki Tüm Sorularınıza Yanıt Bulun.
          </h1>
        </section>

        {loading ? (
          <section className="rounded border border-slate-200 bg-white p-6 text-sm dark:border-slate-800 dark:bg-slate-900">Yükleniyor...</section>
        ) : null}
        {error ? (
          <section className="rounded border border-rose-300 bg-rose-50 p-6 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">{error}</section>
        ) : null}

        {!loading && !error ? (
          <>
            <section className="hidden flex-col gap-16 lg:flex lg:flex-row">
              <aside className="w-full flex-shrink-0 space-y-10 lg:w-72">
                {categories.map((cat, catIndex) => {
                  const isActiveCategory = cat.key === activeCategory?.key;
                  const catItems = Array.isArray(cat.items) ? cat.items : [];
                  return (
                    <section key={cat.key}>
                      <button
                        type="button"
                        onClick={() => selectCategory(cat.key)}
                        className={[
                          'mb-3 flex w-full items-center justify-between border px-4 py-3 text-left transition-all',
                          isActiveCategory
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-slate-200 bg-transparent text-on-surface hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300'
                        ].join(' ')}
                      >
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-xl">{CATEGORY_ICONS[catIndex % CATEGORY_ICONS.length]}</span>
                          <h2 className="font-display text-base font-bold uppercase tracking-wide">{cat.name}</h2>
                        </div>
                        <span className="material-symbols-outlined text-lg">
                          {isActiveCategory ? 'expand_more' : 'chevron_right'}
                        </span>
                      </button>
                      {isActiveCategory ? (
                        <nav className="flex flex-col gap-1 pb-2">
                          {catItems.map((item) => {
                            const isActiveItem = item.id === activeItem?.id;
                            return (
                              <a
                                key={item.id}
                                href={`#faq-answer-${item.id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  selectItem(item.id);
                                }}
                                className={[
                                  'px-4 py-2 text-sm transition-all',
                                  isActiveItem
                                    ? 'border-l-4 border-primary bg-primary/5 font-bold text-primary'
                                    : 'font-medium text-on-surface-variant hover:text-primary dark:text-slate-300'
                                ].join(' ')}
                              >
                                {item.question}
                              </a>
                            );
                          })}
                        </nav>
                      ) : null}
                    </section>
                  );
                })}
              </aside>

              <div className="flex-1">
                {activeItem ? (
                  <article id={`faq-answer-${activeItem.id}`} className="max-w-3xl scroll-mt-28 lg:scroll-mt-32">
                    <h2 className="font-display mb-8 text-2xl font-bold leading-tight text-on-surface md:text-5xl dark:text-white">
                      {activeItem.question}
                    </h2>
                    <div className="bbtv-rich-text prose prose-lg w-full max-w-full space-y-6 text-on-surface-variant dark:text-slate-300">
                      <p className="text-lg font-medium text-on-surface dark:text-white">{activeItem.answer}</p>
                      <blockquote className="my-10 border-l-4 border-primary bg-primary/5 p-8">
                        <p className="font-display text-xl font-bold italic leading-snug text-on-surface dark:text-white">
                          "Mutfaktaki en büyük stres olan 'Ne pişirsem?' sorusunu, veriye dayalı bir çözümle azaltıyoruz."
                        </p>
                      </blockquote>
                    </div>
                    <div className="mt-16 flex flex-col items-center justify-between gap-6 border-t border-slate-200 pt-8 dark:border-slate-800 sm:flex-row">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant dark:text-slate-400">
                        Bu cevap yardımcı oldu mu?
                      </p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => sendVote(activeItem.id, 'yes')}
                          className={`flex items-center gap-2 rounded border px-6 py-2 text-xs font-bold uppercase transition-all ${
                            voteState[activeItem.id] === 'yes' ? 'border-primary text-primary' : 'border-slate-200 text-on-surface'
                          }`}
                        >
                          <span className="material-symbols-outlined text-lg">thumb_up</span>
                          Evet
                        </button>
                        <button
                          type="button"
                          onClick={() => sendVote(activeItem.id, 'no')}
                          className={`flex items-center gap-2 rounded border px-6 py-2 text-xs font-bold uppercase transition-all ${
                            voteState[activeItem.id] === 'no' ? 'border-primary text-primary' : 'border-slate-200 text-on-surface'
                          }`}
                        >
                          <span className="material-symbols-outlined text-lg">thumb_down</span>
                          Hayır
                        </button>
                      </div>
                    </div>
                  </article>
                ) : null}
              </div>
            </section>

            <section className="space-y-8 lg:hidden">
              <section className="space-y-4">
                <div className="rounded-lg bg-white dark:bg-slate-900">
                  <h2 className="mb-4 flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-primary">
                    <span className="material-symbols-outlined text-xl">category</span>
                    Kategoriler
                  </h2>
                  <div className="flex flex-col gap-2">
                    {categories.map((cat) => {
                      const isActive = cat.key === activeCategory?.key;
                      return (
                        <button
                          key={cat.key}
                          type="button"
                          onClick={() => selectCategory(cat.key, true)}
                          className={[
                            'flex items-center gap-3 px-5 py-4 text-left transition-all',
                            isActive
                              ? 'border-l-4 border-primary bg-primary/5 font-bold text-primary'
                              : 'border-l-4 border-transparent bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200'
                          ].join(' ')}
                        >
                          <span className="material-symbols-outlined">{isActive ? 'expand_more' : 'chevron_right'}</span>
                          <span className="flex-1 text-sm uppercase tracking-wide">{cat.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="space-y-6">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-4 dark:border-slate-800">
                  <h3 className="font-display text-xl font-bold text-on-surface dark:text-white">
                    {activeCategory?.name || 'Kategori'}
                  </h3>
                </div>

                {categoryItems.map((item) => {
                  const isOpen = item.id === activeItem?.id;
                  return (
                    <article key={item.id} id={`faq-mobile-answer-${item.id}`} className="scroll-mt-28 overflow-hidden border border-gray-100 bg-white dark:border-slate-800 dark:bg-slate-900">
                      <button
                        type="button"
                        onClick={() => selectItem(item.id)}
                        className="group flex w-full items-center justify-between p-6 text-left"
                      >
                        <span className="pr-4 text-lg font-bold text-on-surface transition-colors group-hover:text-primary dark:text-white">
                          {item.question}
                        </span>
                        <span className={`material-symbols-outlined shrink-0 ${isOpen ? 'text-primary' : 'text-gray-300'}`}>
                          expand_circle_down
                        </span>
                      </button>
                      {isOpen ? (
                        <div className="px-6 pb-8 text-on-surface-variant">
                          <p className="text-base leading-relaxed">{item.answer}</p>
                          <div className="mt-6 flex gap-3">
                            <button
                              type="button"
                              onClick={() => sendVote(item.id, 'yes')}
                              className={`flex items-center gap-2 rounded border px-4 py-2 text-xs font-bold uppercase ${
                                voteState[item.id] === 'yes' ? 'border-primary text-primary' : 'border-slate-200'
                              }`}
                            >
                              <span className="material-symbols-outlined text-base">thumb_up</span>
                              Evet
                            </button>
                            <button
                              type="button"
                              onClick={() => sendVote(item.id, 'no')}
                              className={`flex items-center gap-2 rounded border px-4 py-2 text-xs font-bold uppercase ${
                                voteState[item.id] === 'no' ? 'border-primary text-primary' : 'border-slate-200'
                              }`}
                            >
                              <span className="material-symbols-outlined text-base">thumb_down</span>
                              Hayır
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </section>
            </section>

            <section className="mt-16 border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-900">
              <h2 className="font-display text-3xl font-bold text-on-surface dark:text-white">Hala bir sorunuz mu var?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base text-on-surface-variant dark:text-slate-300">
                Mutfak asistanimiz merak ettiginiz her konuda size yardimci olmaya hazir.
              </p>
              <button
                type="button"
                onClick={openContactModal}
                className="mt-8 rounded bg-primary px-8 py-3 text-sm font-bold uppercase tracking-wide text-white"
              >
                Iletisim Formu
              </button>
            </section>
          </>
        ) : null}
      </main>

      {!embedded ? <Footer /> : null}

      {!embedded ? <BlogBackToTopButton /> : null}

      {isContactModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h3 className="font-display text-2xl font-bold text-on-surface dark:text-white">Iletisim Formu</h3>
              <button
                type="button"
                onClick={closeContactModal}
                className="rounded border border-slate-300 px-2 py-1 text-xs font-bold text-slate-600 hover:text-slate-900 dark:border-slate-600 dark:text-slate-200"
              >
                Kapat
              </button>
            </div>

            <form onSubmit={submitContactForm} className="space-y-4">
              <div>
                <label htmlFor="faq-contact-subject" className="mb-1 block text-sm font-semibold text-on-surface dark:text-slate-100">
                  Konu
                </label>
                <input
                  id="faq-contact-subject"
                  type="text"
                  maxLength={160}
                  value={contactForm.subject}
                  onChange={(e) => setContactForm((prev) => ({ ...prev, subject: e.target.value }))}
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-on-surface dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="Kisa bir konu girin"
                  required
                />
              </div>

              <div>
                <label htmlFor="faq-contact-message" className="mb-1 block text-sm font-semibold text-on-surface dark:text-slate-100">
                  Mesaj
                </label>
                <textarea
                  id="faq-contact-message"
                  rows={6}
                  maxLength={2000}
                  value={contactForm.message}
                  onChange={(e) => setContactForm((prev) => ({ ...prev, message: e.target.value }))}
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-on-surface dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="Detayli mesajinizi yazin"
                  required
                />
              </div>

              {!isInternalUser ? (
                <>
                  <div>
                    <label htmlFor="faq-contact-email" className="mb-1 block text-sm font-semibold text-on-surface dark:text-slate-100">
                      E-posta
                    </label>
                    <input
                      id="faq-contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-on-surface dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      placeholder="ornek@eposta.com"
                      required
                    />
                  </div>

                  <label className="flex items-start gap-2 text-xs text-on-surface-variant dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={contactForm.consentAccepted}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, consentAccepted: e.target.checked }))}
                      className="mt-0.5"
                      required
                    />
                    <span>KVKK kapsaminda verilerimin destek talebi amaciyla islenmesini kabul ediyorum.</span>
                  </label>
                </>
              ) : null}

              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={contactForm.website}
                onChange={(e) => setContactForm((prev) => ({ ...prev, website: e.target.value }))}
                className="hidden"
                aria-hidden="true"
              />

              {contactResult ? (
                <div
                  className={`rounded border px-3 py-2 text-sm ${
                    contactResult.ok
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                      : 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300'
                  }`}
                >
                  {contactResult.message}
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeContactModal}
                  className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 dark:border-slate-600 dark:text-slate-200"
                >
                  Vazgec
                </button>
                <button
                  type="submit"
                  disabled={isContactSubmitting}
                  className="rounded bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
                >
                  {isContactSubmitting ? 'Gonderiliyor...' : 'Gonder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
